/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { View, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { addDays, format, differenceInDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { pd } from '@airasia-phoenix/react-native/utils/utils';
import { useLogger } from '@airasia-rn/core-logger';
import {
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE,
  useAppConfig,
} from '@airasia-rn/ui';
import { useCurrency } from '@airasia-rn/ui/config';
import { useSuperAppContext } from '@airasia-rn/ui/context';
import { ChinaDisclaimerModal } from '../../components';
import {
  CHINA_DOMAIN,
  CHINA_DISCLAIMER_IMAGE_URL,
  HOTEL,
  HOTEL_SEARCH_DEFAULTS,
  MOVE_HOTEL_LIST_PAGE_BASE_URLS,
} from '../../configs/constants';
import { useHotelConfig } from '../../configs/HotelConfigProvider';
import { DialogRefs } from '@airasia-phoenix/react-native/components/DialogView/types';

import { CalendarView } from '../CalendarView';
import GuestFieldView from '../GuestSelectionView/GuestFieldView';
import { DestinationSearchView } from '../DestinationSearchView';
import ButtonView, {
  ButtonViewType,
} from '@airasia-phoenix/react-native/components/ButtonView';
import type { Suggestions, RoomConfig } from '../../types';
import {
  MAX_DATE_OFFSET_DAYS,
  DEFAULT_DEPARTURE_OFFSET_DAYS,
  DEFAULT_RETURN_OFFSET_DAYS,
  DEFAULT_STAY_DAYS,
  DEFAULT_ONE_WAY_STAY_DAYS,
  MIN_DATE_OFFSET_DAYS,
  MIN_RETURN_DATE_OFFSET_DAYS,
  MAX_LOS,
} from '../../configs/constants';
import ERROR_FIELD, { ErrorField } from '../../error';
import { useContinueSearches } from '../../hooks/useContinueSearch';
import { useSearchViewFeatureFlag } from '../../hooks/useSearchViewFeatureFlag';
import {
  useHotelSearchFormState,
  type HotelSearchFormInitialValues,
} from '../../hooks/useHotelSearchFormState';
import { SearchWidgetEvent, SearchWidgetMeta } from '../../analytics';
import { useOptionalHotelsRoutes } from '../../context/HotelsNavigationContext';
import { formatOccupancy } from '../../utils/deeplink';

const calendarConfig = {
  maxLos: MAX_LOS, // The stay cannot exceed this many days
  minDateOffsetDays: MIN_DATE_OFFSET_DAYS,
  minReturnDateOffsetDays: MIN_RETURN_DATE_OFFSET_DAYS,
  maxDateOffsetDays: MAX_DATE_OFFSET_DAYS,
  defaultDepartureOffsetDays: DEFAULT_DEPARTURE_OFFSET_DAYS,
  defaultReturnOffsetDays: DEFAULT_RETURN_OFFSET_DAYS,
  defaultStayDays: DEFAULT_STAY_DAYS,
  defaultOneWayStayDays: DEFAULT_ONE_WAY_STAY_DAYS,
};

type SearchFormData = {
  destination: Suggestions | null;
  checkIn: Date | undefined;
  checkOut: Date | undefined;
  rooms: RoomConfig[];
  destinationChanged?: boolean;
};

export type SearchFormPrefillData = {
  destination?: Suggestions | null;
};

type SearchFormViewProps = {
  onError: (message: string) => void;
  onSubmit?: (searchData: SearchFormData) => void;
  shouldSkipSubmit?: (searchData: SearchFormData) => boolean;
  initialValues?: HotelSearchFormInitialValues;
  prefillData?: SearchFormPrefillData;
  prefillResetKey?: string;
  isMap?: boolean;
};

const SearchFormView = ({
  onError,
  onSubmit,
  shouldSkipSubmit,
  initialValues,
  prefillData,
  prefillResetKey,
  isMap,
}: SearchFormViewProps) => {
  const { t } = useTranslation('hotels');
  const chinaDisclaimerRef = useRef<DialogRefs>(null);
  const { logEvent } = useLogger();
  const { config: appConfig } = useAppConfig();
  const { currency } = useCurrency();
  const { geoId: countryCodeFromContext } = useSuperAppContext();
  const { config: hotelConfig } = useHotelConfig();
  const routes = useOptionalHotelsRoutes();
  const hotelsListRoute = routes?.HOTELS_LIST ?? '/search';
  const { addContinueSearch } = useContinueSearches();
  const { hotel_combined_date_field_enabled } = useSearchViewFeatureFlag();
  const { EVENT_NAMES } = SearchWidgetMeta;
  const destinationPrefill = prefillData?.destination;
  const resolvedInitialValues = useMemo(() => {
    if (!initialValues || !destinationPrefill) {
      return initialValues;
    }

    return {
      ...initialValues,
      destination: destinationPrefill,
    };
  }, [destinationPrefill, initialValues]);

  const { formState, updateFormState } = useHotelSearchFormState(
    resolvedInitialValues,
  );

  const latestFormStateRef = useRef(formState);
  const [destinationFieldError, setDestinationFieldError] = useState<
    ErrorField | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastAppliedDestinationPrefillRef = useRef<string | null>(null);
  const lastAppliedPrefillResetKeyRef = useRef<string | undefined>(
    prefillResetKey,
  );
  const destinationPrefillKey = destinationPrefill
    ? [
        destinationPrefill.id,
        destinationPrefill.aa_id,
        destinationPrefill.name,
        destinationPrefill.full_name,
      ].join('::')
    : null;

  useEffect(() => {
    if (lastAppliedPrefillResetKeyRef.current !== prefillResetKey) {
      lastAppliedPrefillResetKeyRef.current = prefillResetKey;
      lastAppliedDestinationPrefillRef.current = null;
    }

    if (!destinationPrefill || !destinationPrefillKey) {
      return;
    }

    if (lastAppliedDestinationPrefillRef.current === destinationPrefillKey) {
      return;
    }

    lastAppliedDestinationPrefillRef.current = destinationPrefillKey;
    updateFormState({ destination: destinationPrefill });
    setDestinationFieldError(undefined);
  }, [
    destinationPrefill,
    destinationPrefillKey,
    prefillResetKey,
    updateFormState,
  ]);

  useEffect(() => {
    latestFormStateRef.current = formState;
  }, [formState]);

  useEffect(() => {
    return () => {
      if (submitLockTimeoutRef.current) {
        clearTimeout(submitLockTimeoutRef.current);
        submitLockTimeoutRef.current = null;
      }
    };
  }, []);

  const syncFormState = useCallback(
    (updates: Partial<SearchFormData>) => {
      latestFormStateRef.current = {
        ...latestFormStateRef.current,
        ...updates,
      };
      updateFormState(updates);
    },
    [updateFormState],
  );

  // Helper functions for analytics
  const calculateGuestCounts = useCallback((roomsList: RoomConfig[]) => {
    return roomsList.reduce(
      (acc, room) => ({
        adults: acc.adults + room.adults,
        children: acc.children + room.children.length,
        infants: acc.infants + (room.infants || 0),
      }),
      { adults: 0, children: 0, infants: 0 },
    );
  }, []);

  const handleDateConfirm = useCallback(
    (checkIn: Date, checkOut: Date | undefined) => {
      syncFormState({ checkIn, checkOut });
    },
    [syncFormState],
  );

  const handleResetDates = useCallback(() => {
    const defaultCheckIn = addDays(new Date(), DEFAULT_DEPARTURE_OFFSET_DAYS);
    syncFormState({
      checkIn: defaultCheckIn,
      checkOut: addDays(new Date(), DEFAULT_RETURN_OFFSET_DAYS),
    });
  }, [syncFormState]);

  const handleRoomsConfirm = useCallback(
    (updatedRooms: RoomConfig[]) => {
      syncFormState({ rooms: updatedRooms });
    },
    [syncFormState],
  );

  const handleDestinationSelect = useCallback(
    (destination: Suggestions) => {
      syncFormState({ destination });
    },
    [syncFormState],
  );

  const buildChinaDisclaimerRedirectRoute = useCallback(
    (searchData: SearchFormData) => {
      const resolvedCurrency =
        currency ?? appConfig.currency ?? DEFAULT_CURRENCY;
      const resolvedCountryCode =
        hotelConfig?.geoId ??
        appConfig.geoId ??
        countryCodeFromContext ??
        HOTEL_SEARCH_DEFAULTS.COUNTRY_CODE;
      const resolvedLanguageId = appConfig.locale ?? DEFAULT_LANGUAGE;
      const selectedHotelId =
        searchData.destination?.type?.toLowerCase() === HOTEL
          ? searchData.destination.aa_id?.trim()
          : '';
      const routePath =
        appConfig.env === 'prd'
          ? MOVE_HOTEL_LIST_PAGE_BASE_URLS.prod
          : MOVE_HOTEL_LIST_PAGE_BASE_URLS.stg;
      const queryParams = new URLSearchParams({
        currency: resolvedCurrency,
        country_code: resolvedCountryCode,
        checkin: format(searchData.checkIn!, 'yyyy-MM-dd'),
        checkout: format(searchData.checkOut!, 'yyyy-MM-dd'),
        occupancy:
          formatOccupancy(searchData.rooms) || HOTEL_SEARCH_DEFAULTS.OCCUPANCY,
        location_id:
          searchData.destination?.id || HOTEL_SEARCH_DEFAULTS.LOCATION_ID,
        languageId: resolvedLanguageId,
      });

      if (selectedHotelId) {
        queryParams.set('aaId', selectedHotelId);
      }

      return `${routePath}?${queryParams.toString()}`;
    },
    [
      appConfig.currency,
      appConfig.env,
      appConfig.geoId,
      appConfig.locale,
      countryCodeFromContext,
      currency,
      hotelConfig?.geoId,
    ],
  );
  const chinaDisclaimerRedirectRoute = useMemo(() => {
    if (!formState.destination || !formState.checkIn || !formState.checkOut) {
      return hotelsListRoute;
    }

    return buildChinaDisclaimerRedirectRoute({
      destination: formState.destination,
      checkIn: formState.checkIn,
      checkOut: formState.checkOut,
      rooms: formState.rooms,
    });
  }, [
    buildChinaDisclaimerRedirectRoute,
    formState.checkIn,
    formState.checkOut,
    formState.destination,
    formState.rooms,
    hotelsListRoute,
  ]);

  const handleSearch = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    const latestFormState = latestFormStateRef.current;
    const isChinaDomain =
      Platform.OS === 'web' &&
      typeof window !== 'undefined' &&
      window.location.hostname.endsWith(CHINA_DOMAIN);

    // Validate destination is selected
    if (!latestFormState.destination) {
      setDestinationFieldError(ERROR_FIELD.DESTINATION_FIELD);
      return;
    }

    const destination = latestFormState.destination;
    const initialDestination = initialValues?.destination;
    const hasSameLocationAsInitial =
      (initialDestination?.id || '') === (destination.id || '');
    const destinationNameChanged =
      (initialDestination?.name || '').trim().toLowerCase() !==
      (destination.name || '').trim().toLowerCase();
    const destinationHotelChanged =
      (initialDestination?.aa_id || '') !== (destination.aa_id || '');
    const destinationTypeChanged =
      (initialDestination?.type || '') !== (destination.type || '');
    const destinationChanged =
      !initialDestination ||
      !hasSameLocationAsInitial ||
      destinationHotelChanged ||
      destinationNameChanged ||
      destinationTypeChanged;

    // Clear any existing errors
    setDestinationFieldError(undefined);

    const searchData = {
      destination,
      checkIn: latestFormState.checkIn,
      checkOut: latestFormState.checkOut,
      rooms: latestFormState.rooms,
      destinationChanged,
    } satisfies SearchFormData;

    const shouldSkip = shouldSkipSubmit?.(searchData) === true;
    const hasHotelSelectionChangedWithinLocation =
      hasSameLocationAsInitial &&
      (destinationHotelChanged || destinationNameChanged);

    if (shouldSkip && !hasHotelSelectionChangedWithinLocation) {
      return;
    }

    if (isChinaDomain) {
      chinaDisclaimerRef.current?.openModal();
      return;
    }

    // Calculate analytics parameters
    const guestCounts = calculateGuestCounts(latestFormState.rooms);
    const lengthOfStay =
      latestFormState.checkIn && latestFormState.checkOut
        ? differenceInDays(latestFormState.checkOut, latestFormState.checkIn)
        : 0;
    // TODO: need to check all destination data structure has country at end of full_name
    const destinationCountry = destination.full_name?.includes(',')
      ? destination.full_name.split(',').pop()?.trim() || ''
      : '';

    // Fire analytics events
    const analyticsParams = SearchWidgetEvent.searchButtonClickEvent({
      destination: destination.name,
      destinationcategory: destination.type || 'City',
      destinationcountry: destinationCountry || destination.country_code || '',
      checkindate: latestFormState.checkIn
        ? format(latestFormState.checkIn, 'yyyy-MM-dd')
        : '',
      checkoutdate: latestFormState.checkOut
        ? format(latestFormState.checkOut, 'yyyy-MM-dd')
        : '',
      lengthofstay: lengthOfStay,
      rooms: latestFormState.rooms.length,
      totalpax: guestCounts.adults + guestCounts.children + guestCounts.infants,
      adult: guestCounts.adults,
      child: guestCounts.children,
      infant: guestCounts.infants,
      isMap: isMap,
    });
    logEvent(EVENT_NAMES.SEARCH_BUTTON_CLICKED, analyticsParams);

    // Use continue search function instead of mutation
    void addContinueSearch(
      destination,
      latestFormState.checkIn
        ? format(latestFormState.checkIn, 'yyyy-MM-dd')
        : '',
      latestFormState.checkOut
        ? format(latestFormState.checkOut, 'yyyy-MM-dd')
        : '',
      latestFormState.rooms,
    );

    if (onSubmit) {
      setIsSubmitting(true);

      if (submitLockTimeoutRef.current) {
        clearTimeout(submitLockTimeoutRef.current);
      }

      submitLockTimeoutRef.current = setTimeout(() => {
        setIsSubmitting(false);
        submitLockTimeoutRef.current = null;
      }, 2000);

      onSubmit(searchData);
      return;
    }
  }, [
    onSubmit,
    shouldSkipSubmit,
    isSubmitting,
    initialValues?.destination,
    setDestinationFieldError,
    logEvent,
    addContinueSearch,
    calculateGuestCounts,
    buildChinaDisclaimerRedirectRoute,
    EVENT_NAMES.SEARCH_BUTTON_CLICKED,
  ]);

  return (
    <>
      <View style={styles.container}>
        <View style={styles.stationsContainer}>
          <DestinationSearchView
            initialStation={formState.destination}
            onStationSelect={handleDestinationSelect}
            destinationErrorField={destinationFieldError}
            updateDestinationErrorField={setDestinationFieldError}
          />
        </View>
        <View style={styles.calendarAndGuestRow}>
          <View
            style={[
              styles.calendarContainer,
              styles.calendarFieldContainer,
              !hotel_combined_date_field_enabled &&
                styles.calendarContainerSplitDates,
            ]}
          >
            <CalendarView
              checkInDate={formState.checkIn}
              checkOutDate={formState.checkOut}
              fromDateLabel={t('hotelSearchPage.calendar.checkInDate')}
              toDateLabel={t('hotelSearchPage.calendar.checkOutDate')}
              isCombinedField={hotel_combined_date_field_enabled}
              combinedDateLabel={t('hotelSearchPage.calendar.dates', {
                defaultValue: 'Dates',
              })}
              onConfirm={handleDateConfirm}
              onError={onError}
              onReset={handleResetDates}
              config={calendarConfig}
            />
          </View>
          <View style={[styles.guestContainer, styles.guestFieldContainer]}>
            <GuestFieldView
              rooms={formState.rooms}
              onConfirm={handleRoomsConfirm}
            />
          </View>
          <View style={styles.searchButtonContainer}>
            <ButtonView
              text={t('searchSnapPage.searchButton')}
              type={ButtonViewType.PRIMARY_REGULAR}
              onPress={isSubmitting ? undefined : handleSearch}
              testID={`hotel-search-widget-search-button`}
            />
          </View>
        </View>
      </View>

      <ChinaDisclaimerModal
        ref={chinaDisclaimerRef}
        imageViewProps={{
          height: 186,
          width: 186,
          url: CHINA_DISCLAIMER_IMAGE_URL,
        }}
        secondaryText={`${t('searchhotelPage.chinaDiscalimer.redirection')}\n${t(
          'searchhotelPage.chinaDiscalimer.legalDisclaimer',
        )}`}
        redirectRoute={chinaDisclaimerRedirectRoute}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: {
      xs: 'column',
      xl: 'row',
    },
    alignItems: {
      xs: 'stretch',
      xl: 'flex-start',
    },
    gap: {
      xs: pd.ref.space.spacing30.px,
      xl: pd.ref.space.spacing30.px,
    },
    paddingBottom: pd.ref.space.spacing40.px,
    paddingLeft: pd.ref.space.spacing40.px,
    paddingRight: pd.ref.space.spacing40.px,
    paddingTop: pd.ref.space.spacing20.px,
  },
  searchButtonContainer: {
    zIndex: 1,
  },
  calendarAndGuestRow: {
    flex: {
      xl: 1,
    },
    minWidth: 0,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      xs: 'stretch',
      md: 'stretch',
    },
    gap: {
      xs: pd.ref.space.spacing30.px,
      md: pd.ref.space.spacing30.px,
    },
    marginTop: {
      md: pd.ref.space.spacing30.px,
      xl: undefined,
    },
    zIndex: {
      xs: 1,
      xl: 2,
    },
  },
  calendarContainer: {
    // One combined field sizes with the row, so no minimum is needed here.
    flex: 1,
    minWidth: 0,
  },
  calendarContainerSplitDates: {
    // Applied only when the combined-date-field flag is off: two side-by-side
    // fields need the room. 328 rather than the previous 332 so it stops
    // contradicting calendarFieldContainer's 328 xl cap on this same View.
    minWidth: 328,
  },
  calendarFieldContainer: {
    maxWidth: {
      xl: 328,
      md: 390,
    },
  },
  guestContainer: {
    flex: 1,
    minWidth: 0,
  },
  guestFieldContainer: {
    maxWidth: {
      xl: 319.5,
      md: 399,
    },
    minWidth: {
      md: 218,
    },
  },
  stationsContainer: {
    flex: {
      xl: 0.5,
    },
    minWidth: 0,
    width: {
      xs: '100%',
    },
    zIndex: {
      xs: 2,
      xl: 1,
    },
  },
});

export default SearchFormView;
